import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectUser } from "../../store/slices/session/sessionSelector";
import {
  authorizationRequestFrom,
  useApproveMcpAuthorizationMutation,
  useDenyMcpAuthorizationMutation,
  useValidateMcpAuthorizationMutation,
} from "../../store/apis/mcpApi";

/** Mensaje del backend si lo hay; si no, el genérico. 403 = rol sin acceso al MCP. */
function errorMessage(
  error: unknown,
  t: (key: string) => string
): string | null {
  if (!error) return null;
  const { status, data } = error as {
    status?: number;
    data?: { message?: string };
  };
  if (status === 403) return t("mcpAuthorize.forbidden");
  return data?.message || t("mcpAuthorize.genericError");
}

/**
 * Pantalla de consentimiento del MCP de Intelligence. Claude (web, escritorio o Claude
 * Code) manda aquí al usuario al pulsar "Conectar"; con su sesión de siempre, el usuario
 * ve qué aplicación pide acceso y lo permite o lo cancela. En los dos casos se vuelve a
 * la aplicación con la URL que da el backend.
 */
const McpAuthorize: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useSelector(selectUser);
  const request = useMemo(
    () => authorizationRequestFrom(location.search),
    [location.search]
  );

  const [validate, validation] = useValidateMcpAuthorizationMutation();
  const [approve, approval] = useApproveMcpAuthorizationMutation();
  const [deny, denial] = useDenyMcpAuthorizationMutation();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (request) validate(request);
  }, [request, validate]);

  const decide = async (action: typeof approve) => {
    if (!request) return;
    const result = await action(request);
    if ("data" in result && result.data) {
      setRedirecting(true);
      window.location.assign(result.data.data.redirectUrl);
    }
  };

  const info = validation.data?.data;
  const error = !request
    ? t("mcpAuthorize.invalidRequest")
    : errorMessage(validation.error || approval.error || denial.error, t);
  const busy = approval.isLoading || denial.isLoading || redirecting;

  return (
    <Box
      // A pantalla completa y por encima de la barra lateral, que en algunas ramas pinta
      // la propia ruta autenticada: es una pantalla de consentimiento, no una página más
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal,
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        px: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 480, width: "100%" }} elevation={2}>
        {error ? (
          <Stack spacing={2}>
            <Typography variant="h5">{t("mcpAuthorize.errorTitle")}</Typography>
            <Alert severity="error">{error}</Alert>
          </Stack>
        ) : !info ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Typography variant="h5">
              {t("mcpAuthorize.title", { client: info.clientName })}
            </Typography>
            <Typography variant="body1">
              {t("mcpAuthorize.intro", {
                client: info.clientName,
                email: user?.email ?? "",
              })}
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <CheckCircleOutlineIcon color="success" fontSize="small" />
                <Typography variant="body2">
                  {t("mcpAuthorize.canRead")}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <BlockIcon color="action" fontSize="small" />
                <Typography variant="body2">
                  {t("mcpAuthorize.cannotWrite")}
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t("mcpAuthorize.returnTo", { host: info.redirectHost })}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                disabled={busy}
                onClick={() => decide(deny)}
              >
                {t("mcpAuthorize.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => decide(approve)}
              >
                {busy ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("mcpAuthorize.allow")
                )}
              </Button>
            </Stack>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default McpAuthorize;
